import type { Dispatch } from 'redux';
import {
    createDataActionFactory,
    Feature,
    createThunk,
    UnifiedGroup,
    UnifiedGroupRestoreStatus,
    sequentiallyProcessPromiseFactories,
    convertGroupDetailsToUnifiedGroup,
    createActionFactory,
    ThunkDispatch,
    OwsError,
    makeHubGroupPersonaId,
    makeHubPersonaId,
    groupHubMemberFolderUrlKey,
    isDeletedGroup,
    isOwnedGroup,
    ownedUnifiedGroupsLeftNavId,
    joinedUnifiedGroupsLeftNavId,
    deletedUnifiedGroupsLeftNavId,
    isConsumer,
    getMatchingFavoriteUnifiedGroup,
} from '../../../../shared';
import getUserUnifiedGroups from '../../../dataLayer/ows/groups/getUserUnifiedGroups';
import getGroupDetails from '../../../dataLayer/ows/groups/getGroupDetails';
import debounce from 'lodash-es/debounce';
import {
    getPackageState,
    LivePersonaCardInitializationState,
    isInSearchMode,
} from '../../../../appstate';
import { HubState, packageStateKey } from '../../../state';
import getGroupMembershipRequests from '../../../dataLayer/ows/groups/getGroupMembers';
import {
    restoreGroupErrorNotification,
    restoreGroupSuccessNotification,
    groupDeepLinkErrorNotification,
    softDeletedGroupNotification,
} from '../../user/groups/notificationBarActions';
import { prefetchRequestedPersonas } from '../../user/personaListActions';
import restoreGroup from '../../../dataLayer/ows/groups/restoreGroup';
import type { GroupOperationSource } from '../../../models/unifiedGroupOperation';
import { getNextItemInList } from '../../../selectors/personaListSelector';
import { getNextUnifiedGroupIdInList } from '../../../selectors/groups/currentUnifiedGroupSelector';
import getGroupsSettings from '../../../dataLayer/ows/groups/getGroupsSettings';
import { getInitialGroupsToPrefetch } from '../../../selectors/groups/unifiedGroupListSelectorUtils';
import type GetUnifiedGroupsSettingsResponseMessage from 'owa-service/lib/contract/GetUnifiedGroupsSettingsResponseMessage';

/* Time interval to debounce user groups call. This is to prevent an array of
   GetUserUnifiedGroup API calls bombarding the server
*/
const USER_GROUPS_DEBOUNCE_TIME_INTERVAL = 120000;

const FETCH_MEMBERSHIP_REQUESTS_TIMEOUT = 5000;

const MEMBERSHIP_REQUESTS_BATCH_SIZE = 4;

export const initGroupsHubWithPrefetchDataAction = createDataActionFactory<
    {
        unifiedGroups: UnifiedGroup[];
    },
    {}
>('InitGroupsHubWithPrefetchDataAction', Feature.UnifiedGroups);

export const loadUserUnifiedGroupsCompletedAction = createDataActionFactory<
    { unifiedGroups: UnifiedGroup[] },
    {}
>('LoadUserUnifiedGroupsCompletedAction', Feature.UnifiedGroups);

export const fetchGroupsNeedingActionCompletedAction = createDataActionFactory<{}, {}>(
    'FetchGroupsNeedingActionCompletedAction',
    Feature.UnifiedGroups
);

export const getGroupDetailsCompletedAction = createDataActionFactory<{ group: UnifiedGroup }, {}>(
    'GetGroupDetailsCompletedAction',
    Feature.UnifiedGroups
);

export const loadGroupForDeepLinkAction = createActionFactory<{
    hubPersonaId: string;
    groupSmtp: string;
    leftNavItemId?: string;
}>('LoadGroupForDeepLinkAction', Feature.UnifiedGroups, action => ({
    leftNavItemId: action.payload.leftNavItemId,
}));

export const deepLinkGroupUrlClickedAction = createActionFactory<{
    groupSmtp: string;
    tabName?: string;
    source?: string;
}>('DeepLinkGroupUrlClickedAction', Feature.UnifiedGroups, action => ({
    tabName: action.payload.tabName,
    source: action.payload.source,
}));

export const loadGroupFolderForDeepLinkAction = createActionFactory<{
    groupFolder?: string;
    source?: string;
}>('LoadGroupFolderForDeepLinkAction', Feature.UnifiedGroups, action => ({
    groupFolder: action.payload.groupFolder,
    source: action.payload.source,
}));

export const getGroupsSettingsCompletedAction = createDataActionFactory<
    { groupCreationEnabled: boolean; allowAddGuests: boolean; isSensitivityLabelsEnabled: boolean },
    {}
>('GetGroupsSettingsCompletedAction', Feature.UnifiedGroups);

export const restoreGroupClickedAction = createActionFactory<{
    groupId: string;
    source: GroupOperationSource;
    hubPersonaId: string;
}>('RestoreGroupClickedAction', Feature.UnifiedGroups, action => ({
    source: action.payload.source,
}));

export const restorePartiallyRestoredGroupCompletedAction = createActionFactory<{
    groupId: string;
    hubPersonaId: string;
    groupSmtp: string;
}>('RestorePartiallyRestoredGroupCompletedAction', Feature.UnifiedGroups);

export const restoreGroupCompletedAction = createDataActionFactory<
    {
        groupId: string;
        groupSmtp: string;
        groupName: string;
        nextListItemId: string;
        hubPersonaId: string;
    },
    { groupId: string; hubPersonaId: string; errorType: UnifiedGroupRestoreStatus }
>('RestoreGroupCompletedAction', Feature.UnifiedGroups, action => ({
    groupId: action.payload.groupId,
}));

export const getGroupMembershipRequestsCompletedAction = createDataActionFactory<
    {
        groupId: string;
        membershipRequestsCount: number;
        hubPersonaId: string;
    },
    { groupId: string }
>(
    'GetGroupMembershipRequestsCompletedAction',
    Feature.UnifiedGroups,
    action => ({ groupId: action.payload.groupId }),
    action => ({ groupId: action.payload.groupId })
);

export const fetchAllMembershipRequestsCompletedAction = createDataActionFactory<
    {
        totalTimeTakenInMS: number;
    },
    {}
>('FetchAllMembershipRequestsCompletedAction', Feature.UnifiedGroups, action => ({
    totalTimeTakenInMS: action.payload.totalTimeTakenInMS,
}));

export const fetchAllMembershipRequestsTimedOutAction = createActionFactory<{}>(
    'FetchAllMembershipRequestsTimedOutAction',
    Feature.UnifiedGroups
);

export const handleSoftDeletedGroupCompletedAction = createActionFactory<{
    hubPersonaId: string;
    groupId: string;
    mailboxGuid: string;
    isOwner: boolean;
    nextListItemId: string;
    favoriteId: string;
}>('HandleSoftDeletedGroupCompletedAction', Feature.UnifiedGroups);

export const initHubWithBootData = () =>
    createThunk(
        'InitGroupsHubWithBootDataAction',
        (dispatch: ThunkDispatch, getState: () => {}): void => {
            const packageState = getPackageState<HubState>(getState(), packageStateKey);
            const appState = packageState.appState;

            const groupsHubState = packageState.packageState.view.groupsState;
            if (!groupsHubState.hubInitialized) {
                dispatch(
                    initGroupsHubWithPrefetchDataAction.success({
                        unifiedGroups: appState.data.groupsHubState.prefetchState.unifiedGroups,
                    })
                );
            }
        }
    );

export const loadUserUnifiedGroups = debounce(
    (dispatch: ThunkDispatch) => {
        getUserUnifiedGroupsData(dispatch);
    },
    USER_GROUPS_DEBOUNCE_TIME_INTERVAL,
    { leading: true, trailing: false }
);

export const getUserUnifiedGroupsData = (dispatch: ThunkDispatch) => {
    getUserUnifiedGroups()
        .then(groups => {
            dispatch(loadUserUnifiedGroupsCompletedAction.success({ unifiedGroups: groups }));
            dispatch(loadGroupForDeepLinkThunk(groups));
            getMembershipRequestsCount(dispatch, groups);
        })
        .catch((error: Error) => {
            dispatch(loadUserUnifiedGroupsCompletedAction.error());
        });
};

export const getGroupDetailsData = (dispatch: ThunkDispatch) => (
    groupId: string,
    groupSmtp: string,
    hubPersonaId: string,
    tenantId?: string
) =>
    getGroupDetails(groupSmtp, groupId, tenantId)
        .then(response => {
            dispatch(
                getGroupDetailsCompletedAction.success({
                    group: convertGroupDetailsToUnifiedGroup(response.Body.GroupDetails),
                })
            );
        })
        .catch((error: OwsError) => {
            if (error.responseCode === 'ErrorUnifiedGroupMailboxSoftDeleted') {
                dispatch(handleSoftDeletedGroup(hubPersonaId));
            } else {
                // TODO: Notify the failure to user and let her try again
                throw new Error('GetGroupDetails_failed : This should be notified to the user');
            }
        });

export const getGroupDetailsDataForDeepLink = (dispatch: Dispatch) => (groupSmtp: string) => {
    getGroupDetails(groupSmtp)
        .then(response => {
            var group = convertGroupDetailsToUnifiedGroup(response.Body.GroupDetails);
            prefetchRequestedPersonas([group]);
            dispatch(getGroupDetailsCompletedAction.success({ group }));
            dispatch(
                loadGroupForDeepLinkAction({
                    hubPersonaId: group.hubPersonaId,
                    groupSmtp: group.emailAddress,
                })
            );
        })
        .catch((error: Error) => {
            dispatch(loadGroupFolderForDeepLinkAction({ groupFolder: groupHubMemberFolderUrlKey }));
            groupDeepLinkErrorNotification(dispatch)();
        });
};

export const getGroupFolderForDeepLink = (dispatch: Dispatch) => (
    groupFolder?: string,
    source?: string
) => dispatch(loadGroupFolderForDeepLinkAction({ groupFolder, source }));

export const getGroupsSettingsData = (dispatch: Dispatch): void => {
    getGroupsSettings()
        .then(response => {
            dispatch(
                getGroupsSettingsCompletedAction.success({
                    groupCreationEnabled: response.Body.GroupCreationEnabled,
                    allowAddGuests: response.Body.OrgAllowAddGuests,
                    isSensitivityLabelsEnabled: IsSensitivityLabelsEnabled(response.Body),
                })
            );
        })
        .catch((error: Error) => {
            dispatch(getGroupsSettingsCompletedAction.error({}));
        });
};

function IsSensitivityLabelsEnabled(response: GetUnifiedGroupsSettingsResponseMessage): boolean {
    if (
        response.SensitivityLabelPolicy?.SensitivityLabels &&
        response.SensitivityLabelPolicy?.SensitivityLabels.length > 0
    ) {
        return true;
    }

    return false;
}

export const loadGroupForDeepLinkThunk = (groups: UnifiedGroup[]) =>
    createThunk('LoadGroupForDeepLink', (dispatch: ThunkDispatch, getState: () => {}): void => {
        const packageState = getPackageState<HubState>(getState(), packageStateKey);
        if (packageState && packageState.appState.view.navigationState.deepLinkedGroupSmtp) {
            const deepLinkedGroupSmtp =
                packageState.appState.view.navigationState.deepLinkedGroupSmtp;
            const activeGroup = groups.find(element => {
                return element.emailAddress === deepLinkedGroupSmtp && !isDeletedGroup(element);
            });
            // If non deleted group
            if (activeGroup) {
                prefetchRequestedPersonas([activeGroup]);
                if (isOwnedGroup(activeGroup)) {
                    dispatch(
                        loadGroupForDeepLinkAction({
                            hubPersonaId: activeGroup.hubPersonaId,
                            groupSmtp: activeGroup.emailAddress,
                            leftNavItemId: ownedUnifiedGroupsLeftNavId,
                        })
                    );
                } else {
                    dispatch(
                        loadGroupForDeepLinkAction({
                            hubPersonaId: activeGroup.hubPersonaId,
                            groupSmtp: activeGroup.emailAddress,
                            leftNavItemId: joinedUnifiedGroupsLeftNavId,
                        })
                    );
                }
            } else {
                const deletedGroup = groups.find(element => {
                    return element.emailAddress === deepLinkedGroupSmtp && isDeletedGroup(element);
                });
                // if the group is in deleted
                if (deletedGroup) {
                    dispatch(
                        loadGroupForDeepLinkAction({
                            hubPersonaId: deletedGroup.hubPersonaId,
                            groupSmtp: deletedGroup.emailAddress,
                            leftNavItemId: deletedUnifiedGroupsLeftNavId,
                        })
                    );
                } else {
                    // non member group
                    getGroupDetailsDataForDeepLink(dispatch)(deepLinkedGroupSmtp);
                }
            }
        }
    });

export const restoreGroupThunk = (
    groupId: string,
    groupSmtp: string,
    groupName: string,
    hubPersonaId: string,
    routingKey: string,
    isRetry?: boolean
) =>
    createThunk('RestoreGroup', (dispatch: ThunkDispatch, getState: () => {}): void => {
        const packageState = getPackageState<HubState>(getState(), packageStateKey);
        if (packageState) {
            const nextListItemId = getNextUnifiedGroupIdInList(
                packageState.appState,
                packageState.packageState
            );

            restoreGroup(groupId, groupSmtp, routingKey)
                .then(response => {
                    dispatch(
                        restoreGroupCompletedAction.success({
                            groupId,
                            groupSmtp,
                            groupName,
                            nextListItemId,
                            hubPersonaId,
                        })
                    );
                    restoreGroupSuccessNotification(dispatch)(
                        groupId,
                        groupName,
                        makeHubPersonaId(groupSmtp)
                    );
                    getMembershipRequests(dispatch, groupSmtp, groupId);
                })
                .catch((error: OwsError) => {
                    var errorType = getRestoreErrorType(error.responseCode);
                    dispatch(
                        restoreGroupCompletedAction.error({
                            groupId,
                            hubPersonaId,
                            errorType: errorType,
                        })
                    );
                    if (errorType === UnifiedGroupRestoreStatus.RECENTLY_DELETED_FAILED) {
                        restoreGroupErrorNotification(dispatch)(
                            groupId,
                            groupSmtp,
                            groupName,
                            hubPersonaId,
                            routingKey,
                            true
                        );
                    } else if (errorType === UnifiedGroupRestoreStatus.FAILED) {
                        restoreGroupErrorNotification(dispatch)(
                            groupId,
                            groupSmtp,
                            groupName,
                            hubPersonaId,
                            routingKey,
                            isRetry
                        );
                    }
                });
        }
    });

export const getMembershipRequestsCount = (dispatch: ThunkDispatch, groups: UnifiedGroup[]) => {
    const groupIds: { emailAddress: string; externalDirectoryObjectId: string }[] = groups
        .filter(group => group.isOwner && !group.whenSoftDeletedTime)
        .map(group => {
            return {
                emailAddress: group.emailAddress,
                externalDirectoryObjectId: group.externalDirectoryObjectId,
            };
        });

    const batchRequests = (
        groups: { emailAddress: string; externalDirectoryObjectId: string }[]
    ): Promise<void> => {
        return sequentiallyProcessPromiseFactories(
            groups.map(group => () =>
                getMembershipRequests(dispatch, group.emailAddress, group.externalDirectoryObjectId)
            )
        );
    };

    const requestInitiatedTime = new Date().getTime();
    // Split the requests into smaller chunks of size
    const batchPromises = [];
    while (groupIds && groupIds.length > 0) {
        batchPromises.push(batchRequests(groupIds.splice(0, MEMBERSHIP_REQUESTS_BATCH_SIZE)));
    }

    // Wait for all the batches to complete
    Promise.race([
        Promise.all(batchPromises)
            .then(resolve => {
                // Once membership requests fetch is complete, log the time it took for the operation
                dispatch(
                    fetchAllMembershipRequestsCompletedAction.success({
                        totalTimeTakenInMS: new Date().getTime() - requestInitiatedTime,
                    })
                );
                dispatch(prefetchInitialGroupIds());
            })
            .catch(error => {
                dispatch(fetchAllMembershipRequestsCompletedAction.error({}));
                dispatch(prefetchInitialGroupIds());
            }),
        ,
        membershipRequestsTimeout(FETCH_MEMBERSHIP_REQUESTS_TIMEOUT, dispatch),
    ]);
};

export const prefetchInitialGroupIds = () =>
    createThunk('prefetchInitialGroupIds', (dispatch: ThunkDispatch, getState: () => {}): void => {
        const packageState = getPackageState<HubState>(getState(), packageStateKey);
        const appState = packageState.appState;
        const lpcState = appState.view.livePersonaCardInitializationState;
        if (
            Object.keys(appState.data.groupsHubState.unifiedGroupsMap).length > 0 &&
            lpcState === LivePersonaCardInitializationState.Done
        ) {
            const initialGroupsToPrefetch: UnifiedGroup[] = getInitialGroupsToPrefetch(appState);
            prefetchRequestedPersonas(initialGroupsToPrefetch);
        }
    });

export const handleSoftDeletedGroup = (hubPersonaId: string) =>
    createThunk('handleSoftDeletedGroup', (dispatch: ThunkDispatch, getState: () => {}): void => {
        const packageState = getPackageState<HubState>(getState(), packageStateKey);
        if (!packageState) {
            return;
        }
        const groupsMap = packageState.appState.data.groupsHubState.unifiedGroupsMap;
        const isInSearch = isInSearchMode(packageState.appState);
        const nextListItemId =
            getNextItemInList(
                packageState.appState,
                packageState.packageState,
                hubPersonaId,
                isInSearch
            ) || getNextUnifiedGroupIdInList(packageState.appState, packageState.packageState);
        const matchingFavorite = getMatchingFavoriteUnifiedGroup(
            hubPersonaId,
            packageState.appState.data.hubState.favoritePersonsState.favoritePersons
        );
        const clickedHubPersonaId =
            packageState.appState.view.navigationState.clickedHubPersona?.hubPersonaId;
        if (groupsMap?.[hubPersonaId]) {
            const group = groupsMap[hubPersonaId];
            const favoriteId = matchingFavorite ? matchingFavorite.favoriteId : undefined;
            dispatch(
                handleSoftDeletedGroupCompletedAction({
                    hubPersonaId,
                    groupId: group.externalDirectoryObjectId,
                    mailboxGuid: group.mailboxGuid,
                    isOwner: group.isOwner,
                    nextListItemId,
                    favoriteId,
                })
            );
            if (hubPersonaId === clickedHubPersonaId) {
                softDeletedGroupNotification(dispatch)(group.isOwner, group.displayName);
            }
        }
    });

export const getGroupDetailsForPartiallyRestoredGroup = (dispatch: Dispatch) => (
    groups: UnifiedGroup[]
) => {
    groups.forEach(group => {
        if (group) {
            let groupId = group.externalDirectoryObjectId;
            let hubPersonaId = group.hubPersonaId;
            let groupSmtp = group.emailAddress;
            getGroupDetails(group.emailAddress, groupId).then(response => {
                dispatch(
                    getGroupDetailsCompletedAction.success({
                        group: convertGroupDetailsToUnifiedGroup(response.Body.GroupDetails),
                    })
                );
                dispatch(
                    restorePartiallyRestoredGroupCompletedAction({
                        groupId,
                        hubPersonaId,
                        groupSmtp,
                    })
                );
            });
        }
    });
};

function getMembershipRequests(
    dispatch: ThunkDispatch,
    groupSmtp: string,
    groupId: string
): Promise<void> {
    if (isConsumer()) {
        return Promise.resolve();
    }
    return (
        getGroupMembershipRequests(groupId, groupSmtp)
            .then(response => {
                if (response?.Body) {
                    dispatch(
                        getGroupMembershipRequestsCompletedAction.success({
                            groupId,
                            membershipRequestsCount: response.Body.TotalCount,
                            hubPersonaId: makeHubGroupPersonaId(groupId, groupSmtp),
                        })
                    );
                } else {
                    dispatch(
                        getGroupMembershipRequestsCompletedAction.error({
                            groupId,
                        })
                    );
                }
            })
            // Incase if a request fails, proceed further to invoke other requests sequentially
            .catch(error => {
                dispatch(
                    getGroupMembershipRequestsCompletedAction.error({
                        groupId,
                    })
                );
            })
    );
}

/**
 * A Promise returning a timeout. Example use: race between a data call and a timeout
 * @param time Time to wait before resolving (in milliseconds)
 */
function membershipRequestsTimeout(time: number, dispatch: ThunkDispatch): Promise<void> {
    return new Promise<void>(resolve => {
        setTimeout(() => {
            dispatch(fetchAllMembershipRequestsTimedOutAction());
            return resolve();
        }, time);
    });
}

function getRestoreErrorType(errorCode: String): UnifiedGroupRestoreStatus {
    switch (errorCode) {
        case 'ErrorUnifiedGroupExoRestoreFailed':
            return UnifiedGroupRestoreStatus.EXO_FAILED;
        case 'ErrorRestoreUnrestorableGroupFailed':
            return UnifiedGroupRestoreStatus.UNRESTORABLE;
        case 'ErrorRestoreRecentlyDeletedGroupFailed':
            return UnifiedGroupRestoreStatus.RECENTLY_DELETED_FAILED;
        default:
            return UnifiedGroupRestoreStatus.FAILED;
    }
}
export type { DataActionFactory } from '../../../../shared';
export type { Action } from '../../../../shared';
